import {
  Box,
  HStack,
  Badge,
  Text,
  Flex,
  Button,
  Grid,
  GridItem,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalHeader,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Divider,
  UnorderedList,
  ListItem,
  Stack,
  Show,
  Hide,
} from '@chakra-ui/react';
import { useSearchParams } from 'next/navigation';
import {
  BsLine as BsLineIcon,
  BsFillCartFill as BsFillCartFillIcon,
  BsExclamationCircle as BsExclamationCircleIcon,
  BsFillChatFill as BsFillChatFillIcon,
  BsListUl as BsListUlIcon,
} from 'react-icons/bs';
import { MdNotificationsActive as MdNotificationsActiveIcon } from 'react-icons/md';
import { useRouter } from 'next/router';
import { useSetAtom } from 'jotai';
import {
  openLiveChatAtom,
  liveChatSessionVariablesMessageAtom,
} from '@/atoms/livechat';
import { sendAppierEvent } from '@/utils/appier';
import {
  Period24H as Period24HIcon,
  PeriodDay as PeriodDayIcon,
  PeriodNight as PeriodNightIcon,
} from '@/assets/icons';
import { NOT_HEADQUARTER_SERVICE_AREA } from '@/constants';
import SelectProductPane from './SelectProductPane';
import FormatText from '@/components/shared/FormatText';
import { useSubscriptionType } from '@/hooks/useSubscriptionType';

const PeriodTypeBadge = ({ periodType }) => {
  switch (periodType) {
    case 'full_day':
      return (
        <Badge
          variant='bullet'
          bg='#666'
          color='white'
          fontSize={{ base: '2xs', md: 'md' }}>
          <Period24HIcon color='white' />
          全日
        </Badge>
      );
    case 'day':
      return (
        <Badge
          variant='bullet'
          bg='#666'
          color='white'
          fontSize={{ base: '2xs', md: 'md' }}>
          {' '}
          <PeriodDayIcon color='white' />
          日間
        </Badge>
      );
    case 'night':
      return (
        <Badge
          variant='bullet'
          bg='#666'
          color='white'
          fontSize={{ base: '2xs', md: 'md' }}>
          <PeriodNightIcon color='white' />
          夜間
        </Badge>
      );
    default:
      return null;
  }
};

const ProductCard = ({ data, deepLink, isButtonsDisabled }) => {
  // FIXME: 身障應該有其他處理方式
  const isForDisabled = data.name.includes('身障');

  const router = useRouter();
  const searchParams = useSearchParams();
  const queryString = searchParams.toString();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isCardExpanded, onToggle: onToggleCard } = useDisclosure();
  const { isSubscriptionOnly, isHeadquarterSupport, isPmaGov } =
    useSubscriptionType(data?.building_id);
  const type = data.reservation ? '月租' : '月票';

  const isFullyBooked = data?.fully_booked === 1;
  const isSoldOut = isFullyBooked || data?.products?.every((p) => p.is_sold);
  const isContactSupport = Boolean(data?.is_contact_support);
  let spaceBadgeText = '';
  if (data?.reservation && data?.is_fixed_space) {
    spaceBadgeText = '🅿️ 專屬車位';
  } else if (data?.reservation && !data?.is_fixed_space) {
    spaceBadgeText = '🅿️ 自由選位';
  } else if (!data?.reservation && !data?.is_fixed_space) {
    spaceBadgeText = '🅿️ 先到先停';
  }

  const avaliableProductCount = data?.products?.filter(
    (p) => !p.is_sold,
  ).length;
  // 針對須客服處理的產品，目前該產品未處理的租用申請數量
  const applicationCount = data?.application_count || 0;

  let periodType;
  switch (data.period_type) {
    case 'full_day':
      periodType = '全日';
      break;
    case 'day':
      periodType = '日間';
      break;
    case 'night':
      periodType = '夜間';
  }

  const isContactHeadquarterSupport =
    !(
      NOT_HEADQUARTER_SERVICE_AREA &&
      [...NOT_HEADQUARTER_SERVICE_AREA].includes(data.city)
    ) || isHeadquarterSupport;

  // 目前僅區分台北和中部
  const lineUrl = isContactHeadquarterSupport
    ? 'https://lin.ee/AcpIrwm'
    : 'https://lin.ee/CxDqSv1';
  const lineDeepLink = isContactHeadquarterSupport
    ? 'line://oaMessage/@hah6443y/?'
    : 'line://oaMessage/@639wesrc/?';

  const openLiveChat = useSetAtom(openLiveChatAtom);
  const setLiveChatSessionVariablesMessage = useSetAtom(
    liveChatSessionVariablesMessageAtom,
  );

  const questionMessage = encodeURIComponent(
    `我想諮詢停車場名稱：${data.name}($${
      data.month_price ?? '-'
    } TWD/月) ${type} ${periodType} 方案`,
  );

  const handleSubscribe = () => {
    // 如果 type 是月票，則隨機分配一個 product 直接跳轉到訂閱頁面
    if (data?.is_fixed_space) {
      onOpen();
    } else {
      const availableProducts = data.products.filter((p) => !p.is_sold);
      const randomProduct =
        availableProducts[Math.floor(Math.random() * availableProducts.length)];
      router.push(
        `/subscribe/${randomProduct.slug}${
          queryString ? `?${queryString}` : ''
        }`,
      );
    }
  };

  const handleJoinWaitingList = () => {
    const shortId =
      data?.building_id?.length === 36
        ? data.building_id.slice(0, 8)
        : data.building_id;
    if (shortId) {
      router.push(
        `/waitlist/${shortId}${queryString ? `?${queryString}` : ''}`,
      );
    }
  };

  const handleApplySubscription = () => {
    if (isPmaGov && !isForDisabled) {
      window.alert('本月租方案需先至臺北市停管處進行抽籤。');
      window.open('https://monthtkt.pma.gov.tw/park/');
      return;
    }

    const shortId =
      data?.building_id?.length === 36
        ? data.building_id.slice(0, 8)
        : data.building_id;
    if (shortId) {
      router.push(`/apply/${shortId}${queryString ? `?${queryString}` : ''}`);
    }
  };

  const handleAskSupport = () => {
    if (isContactHeadquarterSupport) {
      setLiveChatSessionVariablesMessage(questionMessage);
      openLiveChat();
    }
  };

  return (
    <Box
      p={5}
      borderRadius={16}
      borderWidth='1px'
      borderColor='gray.200'
      backgroundColor='#FCFCFC'
      boxShadow='lg'
      mb={4}>
      <Show below='md'>
        <Text fontSize={'2xl'} mb={3}>
          {data.name}
        </Text>
      </Show>
      <Flex
        fontSize={{ base: 'sm', md: '2xl' }}
        fontWeight='bold'
        alignItems='center'
        justifyContent='space-between'>
        <Flex mb={2} flex='1'>
          <Hide below='md'>
            <h2>
              <Text mr={5}>{data.name} </Text>
            </h2>
          </Hide>
          <HStack spacing={2} mr={4}>
            <PeriodTypeBadge periodType={data.period_type} />
            <Badge
              variant='bullet'
              bg='#cce1f7'
              color='#0067d8'
              fontSize={{ base: '2xs', md: 'md' }}>
              {data.reservation ? '月租' : '月票'}
            </Badge>
          </HStack>
        </Flex>
        <Flex>
          <Text fontWeight='bold' fontSize={{ base: 'lg', md: '2xl' }}>{`$${
            data.month_price ?? '-'
          } `}</Text>
          <Text fontWeight='normal' fontSize={{ base: 'lg', md: '2xl' }}>
            /月
          </Text>
        </Flex>
      </Flex>

      <Flex justifyContent='space-between' flexDirection='column'>
        <Box fontSize={{ base: 'md', md: 'lmdg' }} fontWeight='bold' my={2}>
          {!isSubscriptionOnly ? '車位資訊' : '方案資訊'}
        </Box>
        {!isSubscriptionOnly && (
          <Stack direction='row' fontSize={{ base: 'md', md: 'md' }}>
            {data.tags.map(({ name }, index) => (
              <span key={`${name}-${index}`}>{name}</span>
            ))}
          </Stack>
        )}
        {data?.description ? (
          <Flex
            fontSize='14px'
            mt={4}
            color={'#000'}
            bg='#eee0e0'
            borderRadius={4}
            p={4}>
            <FormatText text={data.description} />
          </Flex>
        ) : null}
        {isCardExpanded && (
          <>
            <Divider my={2} />
            <Flex
              bg='gray.50'
              p={4}
              borderRadius='md'
              my={2}
              direction='column'>
              <Box fontWeight='bold' mb={2}>
                關於此方案
              </Box>
              <Flex fontSize='14px' flexWrap='wrap'>
                <Text noOfLines={1} pr={4}>
                  {spaceBadgeText}
                </Text>
                <Text noOfLines={1} pr={4}>
                  {data?.is_contact_support ? '⏱ 隔日起租' : '⏱ 隔日起租'}
                </Text>
                <Text noOfLines={1} pr={4}>
                  {data
                    ? data.period_type === 'full_day'
                      ? ''
                      : '⏰ 超時臨停'
                    : 'data is undefined'}
                </Text>
                <Text noOfLines={1} pr={4}>
                  📃 開立發票
                </Text>
                <Text noOfLines={1} pr={4}>
                  💳 自動續約
                </Text>
                {/* <Text>超時以小時計費</Text> */}
              </Flex>
              {!data.reservation && (
                <>
                  <Divider my={2} />
                  <Flex
                    alignItems='center'
                    flexWrap='wrap'
                    color='#333'
                    fontSize='14px'>
                    <Box mr={2}>
                      <BsExclamationCircleIcon />
                    </Box>
                    注意：依先到先停為原則，停車場不保留車位；如遇停滿狀況需待車輛離場後再進場
                  </Flex>
                </>
              )}
              <Divider my={2} />
              <Box fontWeight='bold' mb={2}>
                計費 / 取消政策
              </Box>
              <Flex fontSize='14px' direction='column'>
                <UnorderedList mb={2}>
                  <ListItem>
                    若於當月 14 日後開始訂閱，當月租金將與次月租金合併收款
                  </ListItem>
                  <ListItem>
                    欲停止次月自動扣款，請於 26
                    日前至訂單頁面操作「停止自動續約」
                  </ListItem>
                  <ListItem>停止自動續約後，將於次月 1 日起停用</ListItem>
                </UnorderedList>
              </Flex>
              <Flex fontWeight='bold' mb={2} alignItems='center'>
                <Text color='blue.500'>注意</Text>
              </Flex>
              <Flex fontSize='14px' direction='column' color='blue.500'>
                <UnorderedList mb={2}>
                  <ListItem>
                    由於月租/月票型商品為該月份整月租用，
                    訂閱完成恕無法中途取消退費。
                  </ListItem>
                  <ListItem>
                    停放超過月租權益期間將被收取臨停費用，請前往現場繳費機付款出場。
                  </ListItem>
                </UnorderedList>
              </Flex>
            </Flex>
          </>
        )}

        <Grid
          gap='4'
          pt={4}
          templateColumns={{
            base: '1fr',
            lg: '1fr 1fr',
          }}>
          <GridItem>
            <Flex alignItems='center' h='100%'>
              {!isSoldOut && (
                <Text fontSize='md' fontWeight='bold' color='red.600'>{`${
                  !isSubscriptionOnly ? '同類型車位僅剩' : '訂閱方案僅剩'
                } ${isSoldOut ? 0 : avaliableProductCount} ${
                  !isSubscriptionOnly ? '個' : '組'
                }`}</Text>
              )}
            </Flex>
          </GridItem>
          <GridItem display='flex' justifyContent='end'>
            {isSoldOut ? (
              <Button
                onClick={handleJoinWaitingList}
                disabled={isButtonsDisabled}
                leftIcon={<MdNotificationsActiveIcon color='white' />}
                mr={4}
                bg='primary.blue'
                color='white'
                _hover={{
                  bg: 'primary.darkblue',
                }}>
                申請候補
              </Button>
            ) : (
              <>
                {isContactSupport ? (
                  <>
                    <Button
                      isDisabled={
                        applicationCount >= avaliableProductCount ||
                        isButtonsDisabled
                      }
                      onClick={handleApplySubscription}
                      leftIcon={<BsListUlIcon color='white' />}
                      mr={4}
                      bg='primary.blue'
                      color='white'
                      _hover={{
                        bg: 'primary.darkblue',
                      }}>
                      {applicationCount >= avaliableProductCount
                        ? '暫停登記'
                        : isSubscriptionOnly
                        ? '免費預先登記'
                        : '登記租用'}
                    </Button>
                  </>
                ) : (
                  <>
                    {!isSubscriptionOnly && (
                      <Button
                        varient='ghost'
                        bg='primary.white'
                        onClick={() => {
                          if (!isCardExpanded) {
                            // Appier Event 瀏覽方案資訊頁
                            sendAppierEvent('prod_info_viewed', {
                              subs_id: data.id,
                              subs_name: data.name,
                              building_name: data.building_name,
                              period_type: data.period_type,
                              month_price: data?.month_price,
                              url: window?.location?.pathname,
                            });
                          }
                          onToggleCard();
                        }}
                        mr={4}>
                        {isCardExpanded ? '關閉資訊' : '顯示更多資訊'}
                      </Button>
                    )}
                    <Button
                      onClick={handleSubscribe}
                      disabled={isButtonsDisabled}
                      leftIcon={<BsFillCartFillIcon color='white' />}
                      mr={4}
                      bg='primary.blue'
                      color='white'
                      _hover={{
                        bg: 'primary.darkblue',
                      }}>
                      立即訂閱
                    </Button>
                  </>
                )}
              </>
            )}

            {/* {isContactHeadquarterSupport ? (
              <>
                {isSoldOut && (
                  <Button
                    bg='gray.700'
                    color='white'
                    _hover={{
                      bg: 'gray.900',
                    }}
                    leftIcon={<BsFillChatFillIcon color='white' />}
                    onClick={handleAskSupport}
                    >
                    詢問客服
                  </Button>
                )}
              </>
            ) : (
              <Button
                as={'a'}
                leftIcon={<BsLineIcon color='white' />}
                href={lineUrl}
                bg='#57BE37'
                color='white'
                _hover={{
                  bg: 'green.500',
                }}>
                詢問Line客服
              </Button>
            )} */}
          </GridItem>
        </Grid>
      </Flex>

      {Boolean(data.reservation) && (
        <Modal onClose={onClose} isOpen={isOpen} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>選擇車位</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box pb={8}>
                <SelectProductPane
                  onModelClose={onClose}
                  options={data.products.filter(
                    (p) => !p.is_sold && !p.is_contact_support,
                  )}
                />
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export default ProductCard;
